import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { IsoDateTimeSchema } from '../primitives/datetime';
import { PositiveDecimalSchema } from '../primitives/decimal';
import { EmergencyStopIdSchema, PositionIdSchema, SubscriptionIdSchema } from '../primitives/ids';
import { MoneySchema } from '../primitives/money';
import { ReasonCodeSchema } from '../reason-code';

import { TradeSideSchema } from './positions';

/** Body of POST /emergency-stop. The key is required: closing is never pre-checked (I7, FXM-02 §4). */
export const EmergencyStopRequestSchema = z.strictObject({
  closePositions: z
    .boolean()
    .describe('Also close FXMate positions; manual positions are never touched (mockup 8).'),
});
/** Emergency stop body. */
export type EmergencyStopRequest = z.infer<typeof EmergencyStopRequestSchema>;

/** `COMPLETED` when every channel paused and no close failed outright; `PARTIAL` otherwise (M4 R10). */
export const EmergencyStopOutcomeSchema = z.enum(['COMPLETED', 'PARTIAL']);
/** Overall outcome of a stop. */
export type EmergencyStopOutcome = z.infer<typeof EmergencyStopOutcomeSchema>;

/**
 * Pause result of one channel (M4 R10). A failure never cancels the other channels; its
 * `reasonCode` is `EMERGENCY_PAUSE_FAILED` and E10 offers a retry on that row (M4 §4).
 */
export const EmergencyStopChannelResultSchema = z.object({
  subscriptionId: SubscriptionIdSchema,
  channelTitle: z.string().min(1).max(256),
  result: z.enum(['PAUSED', 'FAILED']),
  reasonCode: ReasonCodeSchema.nullable(),
});
/** Pause result of one channel. */
export type EmergencyStopChannelResult = z.infer<typeof EmergencyStopChannelResultSchema>;

/**
 * Close status of one FXMate position (M4 R11, C1 Decision 6). `ALREADY_CLOSED` (closed meanwhile
 * by the market or the trader) counts as a success (FXM-04 §4). `CLOSE_FAILED` stays next to
 * `CLOSING` until the engine retries closes (Q40), so the UI never claims a retry that does not
 * exist. `PENDING_ACCOUNT`: the account is unreachable and the close runs once it is back
 * (FXM-04 §6 case 7).
 */
export const EmergencyStopPositionStatusSchema = z.enum([
  'CLOSED',
  'ALREADY_CLOSED',
  'CLOSING',
  'CLOSE_FAILED',
  'PENDING_ACCOUNT',
]);
/** Close status of one position. */
export type EmergencyStopPositionStatus = z.infer<typeof EmergencyStopPositionStatusSchema>;

/** Close result for one FXMate position; progress is then followed through GET /positions (M4 R12). */
export const EmergencyStopPositionResultSchema = z.object({
  positionId: PositionIdSchema,
  side: TradeSideSchema,
  symbol: z.string().min(1).max(32),
  lots: PositiveDecimalSchema,
  status: EmergencyStopPositionStatusSchema,
  result: MoneySchema.nullable().describe(
    'Realized result in the account currency (Q27); null until realized.',
  ),
  reasonCode: ReasonCodeSchema.nullable(),
});
/** Close result for one position. */
export type EmergencyStopPositionResult = z.infer<typeof EmergencyStopPositionResultSchema>;

/** Response of POST /emergency-stop (C1 §5.2 Decision 6; M4 R10, R11, R17). */
export const EmergencyStopResponseSchema = z.object({
  stopId: EmergencyStopIdSchema,
  stoppedAt: IsoDateTimeSchema,
  outcome: EmergencyStopOutcomeSchema,
  channels: z
    .array(EmergencyStopChannelResultSchema)
    .describe('Every subscription of the trader, paused or failed (FXM-02 §4).'),
  positions: z
    .array(EmergencyStopPositionResultSchema)
    .describe('Empty when closePositions is false.'),
});
/** Emergency stop response. */
export type EmergencyStopResponse = z.infer<typeof EmergencyStopResponseSchema>;

/**
 * POST /emergency-stop. Takes priority over everything; declares no business failure (FXM-02 §7):
 * an engine port that fails shows up as per-item results, never as a 5xx for the whole stop
 * (C1 R9, M4 R10).
 */
export const triggerEmergencyStopRoute = defineRoute({
  operationId: 'triggerEmergencyStop',
  method: 'POST',
  path: '/emergency-stop',
  tag: 'Emergency stop',
  summary: 'Stop everything in one gesture',
  description:
    'Pauses all channels and, if asked, closes FXMate positions; returns the result of each channel ' +
    'and each position. Works even with a channel unreachable or a signal in progress (FXM-02 §7).',
  source: 'technical spec §6; system design §API; FXM-02 §4, §6 case 6, §7; US-14; C1 Decision 6',
  auth: 'bearer',
  provisional: {
    questions: ['Q34', 'Q40', 'Q50', 'Q51'],
    note:
      'on-demand close is missing from the engine and closes are not retried; one tap opens E10 and ' +
      'one confirms; an opening in flight is closed if open when the close list runs.',
  },
  body: { name: 'EmergencyStopRequest', schema: EmergencyStopRequestSchema },
  success: {
    kind: 'json',
    status: 202,
    body: { name: 'EmergencyStopResponse', schema: EmergencyStopResponseSchema },
  },
});
